import { useToast } from '@centraoneplus/ui';
import { errorMessage, useCreateHuhokaLead, useHuhokaLead, type HuhokaLeadOutcome } from './api';

const OUTCOME_LABELS: Record<HuhokaLeadOutcome['status'], string> = {
  created: 'Lead created',
  existing_lead: 'Lead exists',
  existing_client: 'Already a client in HuHoKa CRM',
};

const BADGE =
  'inline-flex h-[30px] items-center rounded-[10px] border border-emerald-300 bg-emerald-50 px-3 text-[12.5px] font-medium text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200';

/**
 * Ticket detail -> HuHoKa CRM lead (plan 14 Phase 4). Once HuHoKa CRM has
 * decided, a badge links to the lead or client there; before that, while the
 * tenant's link is ACTIVE, a **Create lead** button. Creating is idempotent on
 * the ticket (the server never asks HuHoKa twice), and a transient failure is
 * retried in the background, so the button shows "Creating lead…" until done.
 * Renders nothing while loading, when the link is not active, or when the
 * state cannot be read: the rest of the ticket must never depend on it.
 */
export function HuhokaLeadAction({ ticketId, buttonClassName }: { ticketId?: string; buttonClassName: string }) {
  const toast = useToast();
  const { data } = useHuhokaLead(ticketId);
  const create = useCreateHuhokaLead(ticketId);

  if (!data) return null;

  if (data.outcome) {
    const label = OUTCOME_LABELS[data.outcome.status];
    return data.outcome.webUrl ? (
      <a
        href={data.outcome.webUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={BADGE}
        data-testid="huhoka-lead-badge"
        title="Open in HuHoKa CRM"
      >
        {label} ↗
      </a>
    ) : (
      <span className={BADGE} data-testid="huhoka-lead-badge">
        {label}
      </span>
    );
  }

  if (!data.linkActive) return null;

  const pending = create.isPending || data.delivery === 'pending';
  const onClick = async () => {
    try {
      const state = await create.mutateAsync();
      if (state.outcome) {
        toast.success('HuHoKa CRM', OUTCOME_LABELS[state.outcome.status]);
      } else if (state.delivery === 'failed') {
        toast.error('HuHoKa CRM', state.lastError ?? 'HuHoKa CRM could not create the lead.');
      } else {
        toast.info('HuHoKa CRM', 'HuHoKa CRM is busy. The lead will be created shortly.');
      }
    } catch (err) {
      toast.error('HuHoKa CRM', errorMessage(err, 'The lead could not be created. Try again.'));
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className={buttonClassName}
      title={
        data.delivery === 'failed' && data.lastError
          ? `Last attempt failed: ${data.lastError}`
          : 'Create a lead for this requester in HuHoKa CRM'
      }
      data-testid="huhoka-create-lead"
    >
      {pending ? 'Creating lead…' : 'Create lead'}
    </button>
  );
}
